// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Constants } from '../../Constants';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert, updateTokenUsage } from '../../redux/features/app/appSlice';
import { ChatScope, ChatState } from '../../redux/features/conversations/ChatState';
import { Conversations } from '../../redux/features/conversations/ConversationsState';
import {
    addConversation,
    addMessagesToConversation,
    setConversations,
    setSelectedConversation,
    updateBotResponseStatus,
} from '../../redux/features/conversations/conversationsSlice';
import { Plugin } from '../../redux/features/plugins/PluginsState';
import { AuthHelper } from '../auth/AuthHelper';
import { AlertType } from '../models/AlertType';
import { Bot } from '../models/Bot';
import { ChatMessageType, IChatExtendedDetails, IChatMessage, IChatSearchQuery } from '../models/ChatMessage';
import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { IChatUser } from '../models/ChatUser';
import { TokenUsage } from '../models/TokenUsage';
import { IAskVariables } from '../semantic-kernel/model/Ask';
import { BotService } from '../services/BotService';
import { ChatService } from '../services/ChatService';
import { DocumentDownloadService } from '../services/DocumentDownloadService';
import { DocumentImportService } from '../services/DocumentImportService';

import theme1Icon from '../../assets/bot-icons/theme1_Icon.png';
import { ChatMessageService } from '../services/ChatMessageService';

export interface GetResponseOptions {
    messageType: ChatMessageType;
    value: string;
    chatId: string;
    contextVariables?: IAskVariables[];
}

export const useChat = () => {
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const botService = new BotService(process.env.REACT_APP_BACKEND_URI as string);
    const chatService = new ChatService(process.env.REACT_APP_BACKEND_URI as string);
    const chatMessageService = new ChatMessageService(process.env.REACT_APP_BACKEND_URI as string);
    const documentImportService = new DocumentImportService(process.env.REACT_APP_BACKEND_URI as string);

    const botProfilePictures: string[] = [theme1Icon];

    const userId = activeUserInfo?.id ?? '';
    const fullName = activeUserInfo?.username ?? '';
    const emailAddress = activeUserInfo?.email ?? '';
    const loggedInUser: IChatUser = {
        id: userId,
        fullName,
        emailAddress,
        photo: undefined, // TODO: [Issue #45] Make call to Graph /me endpoint to load photo
        online: true,
        isTyping: false,
    };

    const { plugins } = useAppSelector((state: RootState) => state.plugins);

    const getChatUserById = (id: string, chatId: string, users: IChatUser[]) => {
        if (id === `${chatId}-bot` || id.toLocaleLowerCase() === 'bot') return Constants.bot.profile;
        return users.find((user) => user.id === id);
    };

    const createChat = async () => {
        const chatTitle = `iCON GPT @ ${new Date().toLocaleString()}`;
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

        let chatScope = ChatScope.None;
        const lastSelectedChatScope = localStorage.getItem('lastSelectedChatScope');
        if (lastSelectedChatScope && +lastSelectedChatScope) {
            chatScope = +lastSelectedChatScope as ChatScope;
        }

        try {
            await chatService
                .createChatAsync(userId, chatTitle, accessToken, chatScope)
                .then((result: ICreateChatSessionResponse) => {
                    const newChat: ChatState = {
                        id: result.chatSession.id,
                        title: result.chatSession.title,
                        systemDescription: result.chatSession.systemDescription,
                        memoryBalance: result.chatSession.memoryBalance,
                        messages: [result.initialBotMessage],
                        users: [loggedInUser],
                        botProfilePicture: getBotProfilePicture(Object.keys(conversations).length),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        isHidden: false,
                        lastUpdatedTimestamp: Date.parse(new Date().toISOString()),
                        chatScope,
                    };
                    dispatch(addConversation(newChat));
                    return newChat.id;
                });
        } catch (e: any) {
            const errorMessage = `Unable to create new chat. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const getResponse = async ({ messageType, value, chatId, contextVariables }: GetResponseOptions) => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const useSemanticSearch = urlParams.get('semantic');
        const semanticConfigName = urlParams.get('config');

        const ask = {
            input: value,
            variables: [
                {
                    key: 'userId',
                    value: userId,
                },
                {
                    key: 'userName',
                    value: fullName,
                },
                {
                    key: 'chatId',
                    value: chatId,
                },
                {
                    key: 'messageType',
                    value: messageType.toString(),
                },
                {
                    key: 'useSemanticSearch',
                    value: useSemanticSearch ?? '',
                },
                {
                    key: 'semanticConfigName',
                    value: semanticConfigName ?? '',
                },
            ],
        };

        if (contextVariables) {
            ask.variables.push(...contextVariables);
        }

        try {
            const askResult = await chatService
                .getBotResponseAsync(
                    ask,
                    await AuthHelper.getSKaaSAccessToken(instance, inProgress),
                    getEnabledPlugins(),
                )
                .catch((e: any) => {
                    throw e;
                });

            // Update token usage of current session
            const responseTokenUsage = askResult.variables.find((v) => v.key === 'tokenUsage')?.value;
            if (responseTokenUsage) dispatch(updateTokenUsage(JSON.parse(responseTokenUsage) as TokenUsage));
        } catch (e: any) {
            dispatch(updateBotResponseStatus({ chatId, status: undefined }));
            const errorMessage = `Unable to generate bot response. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const loadChats = async (showHidden?: boolean | undefined) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            const chatSessions = await chatService.getAllChatsAsync(userId, accessToken, showHidden ?? false);

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (chatSessions !== null && chatSessions.length > 0) {
                const loadedConversations: Conversations = {};
                for (const chatSession of chatSessions) {
                    const chatMessages: IChatMessage[] = [];
                    const chatUsers: IChatUser[] = [];

                    loadedConversations[chatSession.id] = {
                        id: chatSession.id,
                        title: chatSession.title,
                        systemDescription: chatSession.systemDescription,
                        memoryBalance: chatSession.memoryBalance,
                        users: chatUsers,
                        messages: chatMessages,
                        botProfilePicture: getBotProfilePicture(Object.keys(loadedConversations).length),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        isHidden: chatSession.isHidden,
                        lastUpdatedTimestamp: chatSession.lastModifiedOn,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/prefer-nullish-coalescing
                        chatScope: chatSession.chatScope === undefined ? ChatScope.None : chatSession.chatScope,
                    };
                }

                // get the id of the loadedConversations with the latest lastUpdatedTimestamp
                // const latestChatId = Object.values(loadedConversations).reduce((prev, current) =>
                //     // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                //     new Date(prev.lastUpdatedTimestamp!).getTime() > new Date(current.lastUpdatedTimestamp!).getTime()
                //         ? prev
                //         : current,
                // ).id;

                const latestChatId = chatSessions[0].id;

                dispatch(setConversations(loadedConversations));
                if (selectedId === latestChatId) {
                    dispatch(setSelectedConversation(''));
                    setTimeout(() => {
                        dispatch(setSelectedConversation(latestChatId));
                    }, 50);
                } else {
                    dispatch(setSelectedConversation(latestChatId));
                }
            } else {
                // No chats exist, create first chat window
                await createChat();
            }

            return true;
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    const loadHiddenChats = async () => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            const chatSessions = await chatService.getHiddenChatsAsync(userId, accessToken);

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (chatSessions !== null && chatSessions.length > 0) {
                const loadedConversations: Conversations = {};
                for (const chatSession of chatSessions) {
                    const chatMessages: IChatMessage[] = [];
                    const chatUsers: IChatUser[] = [];

                    loadedConversations[chatSession.id] = {
                        id: chatSession.id,
                        title: chatSession.title,
                        systemDescription: chatSession.systemDescription,
                        memoryBalance: chatSession.memoryBalance,
                        users: chatUsers,
                        messages: chatMessages,
                        botProfilePicture: getBotProfilePicture(Object.keys(loadedConversations).length),
                        input: '',
                        botResponseStatus: undefined,
                        userDataLoaded: false,
                        isHidden: chatSession.isHidden,
                        lastUpdatedTimestamp: chatSession.lastModifiedOn,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/prefer-nullish-coalescing
                        chatScope: chatSession.chatScope === undefined ? ChatScope.None : chatSession.chatScope,
                    };
                }

                // get the id of the loadedConversations with the latest lastUpdatedTimestamp
                // const latestChatId = Object.values(loadedConversations).reduce((prev, current) =>
                //     // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                //     new Date(prev.lastUpdatedTimestamp!).getTime() > new Date(current.lastUpdatedTimestamp!).getTime()
                //         ? prev
                //         : current,
                // ).id;

                const latestChatId = chatSessions[0].id;

                dispatch(setConversations(loadedConversations));
                if (selectedId === latestChatId) {
                    dispatch(setSelectedConversation(''));
                    setTimeout(() => {
                        dispatch(setSelectedConversation(latestChatId));
                    }, 50);
                } else {
                    dispatch(setSelectedConversation(latestChatId));
                }
            } else {
                dispatch(setConversations({}));
                dispatch(setSelectedConversation(null));
            }

            return true;
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            console.error(errorMessage);

            dispatch(setConversations({}));
            dispatch(setSelectedConversation(null));

            return false;
        }
    };

    const getChatMessages = async (chatId: string): Promise<IChatMessage[]> => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            // get messages and update the redux store
            const messages = await chatService.getChatMessagesAsync(chatId, 0, 100, accessToken);
            dispatch(addMessagesToConversation({ chatId, messages }));
            return messages;
        } catch (e: any) {
            const errorMessage = `Unable to get chat messages. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const downloadBot = async (chatId: string) => {
        try {
            return await botService.downloadAsync(chatId, await AuthHelper.getSKaaSAccessToken(instance, inProgress));
        } catch (e: any) {
            const errorMessage = `Unable to download the bot. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return undefined;
    };

    const uploadBot = async (bot: Bot) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        botService
            .uploadAsync(bot, userId, accessToken)
            .then(async (chatSession: IChatSession) => {
                const chatMessages = await chatService.getChatMessagesAsync(chatSession.id, 0, 100, accessToken);

                const newChat = {
                    id: chatSession.id,
                    title: chatSession.title,
                    users: [loggedInUser],
                    messages: chatMessages,
                    botProfilePicture: getBotProfilePicture(Object.keys(conversations).length),
                    botResponseStatus: undefined,
                };

                dispatch(addConversation(newChat));
            })
            .catch((e: any) => {
                const errorMessage = `Unable to upload the bot. Details: ${getErrorDetails(e)}`;
                dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
            });
    };

    const getBotProfilePicture = (index: number): string => {
        return botProfilePictures[index % botProfilePictures.length];
    };

    const getChatMemorySources = async (chatId: string) => {
        try {
            return await chatService.getChatMemorySourcesAsync(
                chatId,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to get chat files. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const getSemanticMemories = async (chatId: string, memoryName: string) => {
        try {
            return await chatService.getSemanticMemoriesAsync(
                chatId,
                memoryName,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to get semantic memories. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return [];
    };

    const importDocument = async (chatId: string, files: File[]) => {
        try {
            await documentImportService.importDocumentAsync(
                userId,
                fullName,
                chatId,
                files,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Failed to upload document. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    /*
     * Once enabled, each plugin will have a custom dedicated header in every Semantic Kernel request
     * containing respective auth information (i.e., token, encoded client info, etc.)
     * that the server can use to authenticate to the downstream APIs
     */
    const getEnabledPlugins = () => {
        return Object.values<Plugin>(plugins).filter((plugin) => plugin.enabled);
    };

    const joinChat = async (chatId: string) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            await chatService.joinChatAsync(userId, chatId, accessToken).then(async (result: IChatSession) => {
                // Get chat messages
                const chatMessages = await chatService.getChatMessagesAsync(result.id, 0, 100, accessToken);

                // Get chat users
                const chatUsers = await chatService.getAllChatParticipantsAsync(result.id, accessToken);

                const newChat: ChatState = {
                    id: result.id,
                    title: result.title,
                    systemDescription: result.systemDescription,
                    memoryBalance: result.memoryBalance,
                    messages: chatMessages,
                    users: chatUsers,
                    botProfilePicture: getBotProfilePicture(Object.keys(conversations).length),
                    input: '',
                    botResponseStatus: undefined,
                    userDataLoaded: false,
                };

                dispatch(addConversation(newChat));
            });
        } catch (e: any) {
            const errorMessage = `Error joining chat ${chatId}. Details: ${getErrorDetails(e)}`;
            return { success: false, message: errorMessage };
        }

        return { success: true, message: '' };
    };

    const editChat = async (
        chatId: string,
        title: string,
        syetemDescription: string,
        memoryBalance: number,
        scope?: ChatScope,
        isHidden?: boolean,
    ) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
        try {
            await chatService.editChatAsync(
                chatId,
                title,
                syetemDescription,
                memoryBalance,
                accessToken,
                scope,
                isHidden,
                userId,
            );
        } catch (e: any) {
            const errorMessage = `Error editing chat ${chatId}. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const downloadAttachment = async (fileRelativePath: string) => {
        try {
            return await new DocumentDownloadService(process.env.REACT_APP_BACKEND_URI as string).downloadDocumentAsync(
                fileRelativePath,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to download attachment. Either the attachment does not exist or there was an error downloading the attachment`;
            console.error(`Unable to download attachment. Details: ${getErrorDetails(e)}`);
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return undefined;
    };

    const downloadExtendedDetails = async (extendedDetails?: IChatExtendedDetails) => {
        try {
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
            return await new DocumentDownloadService(
                process.env.REACT_APP_BACKEND_URI as string,
            ).downloadExtendedDetailsDocumentAsync(
                extendedDetails?.query ?? '',
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                extendedDetails?.useSemanticSearch ?? false,
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                extendedDetails?.useSemanticSearch ? extendedDetails.semanticConfigName ?? 'advania-semantic-config' : '',
                extendedDetails?.filter ?? '',
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to download items export.`;
            console.error(`Unable to download attachment. Details: ${getErrorDetails(e)}`);
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return undefined;
    };

    const downloadExtendedDetailsAsString = async (extendedDetails?: IChatExtendedDetails) => {
        try {
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
            return await new DocumentDownloadService(
                process.env.REACT_APP_BACKEND_URI as string,
            ).downloadExtendedDetailsDocumentAsStringAsync(
                extendedDetails?.query ?? '',
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                extendedDetails?.useSemanticSearch ?? false,
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                extendedDetails?.useSemanticSearch ? extendedDetails.semanticConfigName ?? 'advania-semantic-config' : '',
                extendedDetails?.filter ?? '',
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to download items export.`;
            console.error(`Unable to download attachment. Details: ${getErrorDetails(e)}`);
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }

        return undefined;
    };

    const getArchiveMemoryDetails = async (searchQuery?: IChatSearchQuery) => {
        try {
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
            return await chatMessageService.getArchiveMemoryDetailsAsync(
                searchQuery?.query ?? '',
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                searchQuery?.useSemanticSearch ?? false,
                // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
                searchQuery?.useSemanticSearch ? searchQuery.semanticConfigName ?? 'advania-semantic-config' : '',
                searchQuery?.filter ?? '',
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
        } catch (e: any) {
            const errorMessage = `Unable to get archive memory details.`;
            console.error(`${errorMessage} Details: ${getErrorDetails(e)}`);
        }

        return [];
    };

    return {
        getChatUserById,
        createChat,
        loadChats,
        loadHiddenChats,
        getChatMessages,
        getResponse,
        downloadBot,
        uploadBot,
        getChatMemorySources,
        getSemanticMemories,
        importDocument,
        joinChat,
        editChat,
        downloadAttachment,
        downloadExtendedDetails,
        downloadExtendedDetailsAsString,
        getArchiveMemoryDetails,
    };
};

function getErrorDetails(e: any) {
    return e instanceof Error ? e.message : String(e);
}
